/**
 * Moving around a stop's checklist freely (Brad, field test 2026-09-14): the
 * driver standing at the feeder shoots the feeder, not the four items before
 * it. Pure functions, so the rules are pinned by tests rather than screens.
 */
import type { DraftItem } from './drafts'

/** Chip labels for the item row at the top of the checklist. */
export const ITEM_SHORT: Record<string, string> = {
  bag_placed: 'Bag',
  water: 'Water',
  feeder: 'Feeder',
  grit: 'Grit',
  oyster_shell: 'Shell',
  gate_closed: 'Gate',
  wide_shot: 'Wide shot',
}

/** The access-note fields that say where each checklist item is, so the stop
 *  screen can show "Feeder - photograph any bin" as one tappable row. */
export const ITEM_ACCESS_FIELDS: Record<string, { label: string; field: string }[]> = {
  bag_placed: [{ label: 'Feed storage', field: 'feed_storage_location' }],
  water: [{ label: 'Waterer', field: 'waterer_location' }],
  feeder: [{ label: 'Feeder', field: 'feeder_location' }],
  grit: [{ label: 'Grit', field: 'grit_location' }],
  oyster_shell: [{ label: 'Oyster shell', field: 'shell_location' }],
  gate_closed: [
    { label: 'Gate', field: 'gate_location' },
    { label: 'Latch', field: 'latch_notes' },
  ],
  wide_shot: [],
}

export type ItemState = 'todo' | 'done' | 'skipped'

/** Done = a photo or a condition. A skip with neither is 'skipped'. */
export function itemState(item: DraftItem | undefined): ItemState {
  if (!item) return 'todo'
  if (item.hasPhoto || item.condition) return 'done'
  if (item.skipped) return 'skipped'
  return 'todo'
}

/**
 * Where to go after the driver answers the item at `from`: the next item not
 * yet answered, looking forward and then wrapping round to any skipped past by
 * jumping ahead. Null when every item has a photo, a condition or a skip -
 * the stop is ready to finish.
 */
export function nextOpenIndex(required: string[], items: Record<string, DraftItem>, from: number): number | null {
  const n = required.length
  for (let step = 1; step <= n; step++) {
    const i = (from + step) % n
    if (itemState(items[required[i]]) === 'todo') return i
  }
  return null
}
